import { useState, useCallback, useRef, useEffect } from 'react';

interface UseProgressiveLoadingOptions {
  initialVisibleItems?: number;
  loadMoreBatchSize?: number;
  totalItems: number;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
}

export function useProgressiveLoading({
  initialVisibleItems = 10,
  loadMoreBatchSize = 10,
  totalItems,
  onLoadMore,
  hasMoreData = true,
}: UseProgressiveLoadingOptions) {
  const [visibleItemCount, setVisibleItemCount] = useState(initialVisibleItems);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Load more items when the sentinel comes into view
  const loadMore = useCallback(() => {
    // If we have more visible items to show, show them
    if (visibleItemCount < totalItems) {
      setVisibleItemCount(prev => Math.min(prev + loadMoreBatchSize, totalItems));
    }

    // If we're close to showing all available items and there's more data to fetch
    if (hasMoreData && totalItems - visibleItemCount <= loadMoreBatchSize && onLoadMore) {
      onLoadMore();
    }
  }, [visibleItemCount, totalItems, loadMoreBatchSize, hasMoreData, onLoadMore]);

  // Intersection observer for load more
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore]);

  // Reset visible count when total items change (e.g., filter change)
  useEffect(() => {
    setVisibleItemCount(initialVisibleItems);
  }, [totalItems, initialVisibleItems]);

  const hasMoreItems = visibleItemCount < totalItems || hasMoreData;
  const isLoadingMore = hasMoreItems;

  return {
    visibleItemCount,
    loadMoreRef,
    hasMoreItems,
    isLoadingMore,
    loadMore: () => loadMore(), // Manual trigger if needed
  };
}
